import { resolveCommissionGoals, isCommissionComplete, type CommissionGoalFacts } from '../commission-goals';
import type { EssenceType } from './essence';
import type { CrucibleContentOverrides } from './session';
import type { VeilSaturationId } from './veil-saturation';
import type { CrucibleLocationId } from './crucible-location';

/**
 * Commissions: requests posted to a veil-touched character, such as a house of
 * exchange asking for essence from a particular Crucible. Authored as JSON in
 * `game-content/data/commissions/<id>.commission.json`; a character reads the
 * ones it holds from `GET .../characters/:characterId/commissions/:commissionId`. See docs/gameplay/game-intro.md ("The First
 * Commission").
 */

/** Identifies what kind of commission a character holds: the commission file's id. */
export type CommissionId = string;

/** The commission every new character is offered, and that locks the Crucibles listing until it's complete. */
export const INTRO_COMMISSION_ID: CommissionId = 'intro-first-commission';

/** Lifecycle of a commission held by one character. */
export type CharacterCommissionStatus = 'offered' | 'accepted' | 'completed';

/** Every commission status; a new one fails to compile until it is listed here. */
const CHARACTER_COMMISSION_STATUS_KEYS: Readonly<Record<CharacterCommissionStatus, true>> = { offered: true, accepted: true, completed: true };

export function isCharacterCommissionStatus(value: unknown): value is CharacterCommissionStatus {
    return typeof value === 'string' && Object.hasOwn(CHARACTER_COMMISSION_STATUS_KEYS, value);
}

/** One commission held by one character. */
export interface CharacterCommission {
    /** Unique id of this commission row. */
    id: string;
    characterId: string;
    commissionId: CommissionId;
    status: CharacterCommissionStatus;
    /**
     * The commission's goals as they were when it was offered, so a later
     * content edit never changes an offer already made.
     */
    goals: CommissionGoal[];
    /** The Crucible run this commission applies to, set when it is accepted. */
    crucibleId: string | null;
    offeredAt: string;
    acceptedAt: string | null;
    completedAt: string | null;
}

/** Kinds of commission goal. New kinds extend `CommissionGoal`. */
export type CommissionGoalType = 'essence-gather' | 'destroy-heart';

/** Every goal type; a new one fails to compile until it is listed here. */
const COMMISSION_GOAL_TYPE_KEYS: Readonly<Record<CommissionGoalType, true>> = { 'essence-gather': true, 'destroy-heart': true };

export function isCommissionGoalType(value: unknown): value is CommissionGoalType {
    return typeof value === 'string' && Object.hasOwn(COMMISSION_GOAL_TYPE_KEYS, value);
}

/** Something that must be true before a goal is active. Same shape as `CommissionRequirement`. */
export type CommissionGoalRequirement =
    /** Another goal of the same commission is complete. */
    | { type: 'goal-completed'; goal: string };

/** Fields every commission goal has. */
interface CommissionGoalBase {
    /** Stable within its commission; requirements, progress and the Journal refer to it. */
    id: string;
    type: CommissionGoalType;
    /** Player-facing text, authored as written (e.g. "Carry out 60 Memory essence"). */
    description: string;
    /** A bonus goal: not needed to complete the commission. Default false. */
    isOptional?: boolean;
    /**
     * All must be met before the goal is active; until then it is locked. A
     * destroy-heart goal that requires a gather goal keeps the heart
     * unbreakable until the gather is done.
     */
    requires?: CommissionGoalRequirement[];
    /** Hide the goal while it is locked, revealing it once active. Default false. */
    isHiddenWhileLocked?: boolean;
    /**
     * Whose progress counts: each party member on their own ('individual',
     * the default) or the party combined ('party').
     */
    scope?: 'individual' | 'party';
}

/** Collect essence during the run. */
export interface EssenceGatherGoal extends CommissionGoalBase {
    type: 'essence-gather';
    /**
     * How much to collect: units of `essenceType` when it is set, otherwise
     * exchange value across all types (the units of the "You banked N
     * Essence" summary).
     */
    target: number;
    /** Count only this essence type. */
    essenceType?: EssenceType;
    /** On completion the issuer takes `target` from what was collected. Default true. */
    isHandedOver?: boolean;
}

/** Shatter the Crucible's heart. */
export interface DestroyHeartGoal extends CommissionGoalBase {
    type: 'destroy-heart';
}

/** One goal of a commission. */
export type CommissionGoal = EssenceGatherGoal | DestroyHeartGoal;

/** Lifecycle of one goal during a run. */
export type CommissionGoalStatus = 'locked' | 'active' | 'complete';

/** A goal's state in a run: computed by the run, never authored. */
export interface CommissionGoalProgress {
    goalId: string;
    status: CommissionGoalStatus;
    /** Progress toward `target`, for countable goals (essence-gather). */
    current?: number;
    target?: number;
}

/** The commission a Crucible run was provisioned for, as the run carries it. */
export interface CrucibleCommission {
    commissionId: CommissionId;
    title: string;
    issuer: string;
    goals: CommissionGoal[];
}

/** What a run reports to a player about its commission: the goals and their progress for that player. */
export interface CrucibleCommissionState extends CrucibleCommission {
    progress: CommissionGoalProgress[];
}

/**
 * Fixed setup for a purpose-built Crucible that a commission sends a
 * character into. Every field a client is never allowed to ask for itself
 * (saturation, seed, which manifestations spawn and what they drop) is
 * pinned here, so accepting the commission always produces the same run.
 */
export interface CommissionCrucibleConfig {
    displayName: string;
    mapFile: string;
    maxPlayers: number;
    veilSaturation: VeilSaturationId;
    /** Where the Crucible surfaced, when its story places it; rolled like any other Crucible's when omitted. */
    location?: CrucibleLocationId;
    /** Pinned so every player gets the same enemies and essence pool. */
    generationSeed: string;
    /** Purpose-built content: which enemies spawn and what they drop. */
    contentOverrides: CrucibleContentOverrides;
    /**
     * Cutscenes (ids; a character loads them from `GET .../characters/:characterId/cutscenes/:cutsceneId`) that bracket the run.
     * Omitted entirely for a Crucible with no story beats.
     */
    cutscenes?: CrucibleCutscenes;
    /**
     * A vision (id in `game-content/data/visions/`) every member still in the
     * run receives when its heart shatters: a guaranteed vision, like the
     * intro Crucible's first.
     */
    vision?: string;
}

/** Cutscene ids attached to a Crucible's setup. */
export interface CrucibleCutscenes {
    /** Played before the run starts. */
    before?: string;
    /** Played after a victory, in place of the results screen. */
    afterVictory?: string;
    /** Played after a defeat (a party wipe), in place of the results screen. */
    afterDefeat?: string;
}

/**
 * An offer cutscene a character still has to see: the commission is offered
 * and its `offerCutscene` is unseen at its current version. Listed by
 * `GET /api/accounts/:id/characters/:characterId/commissions/cutscenes`.
 */
export interface PendingOfferCutscene {
    commissionId: CommissionId;
    cutsceneId: string;
}

/**
 * A commission type as players see it, from
 * `GET .../characters/:characterId/commissions/:commissionId` (only for a
 * commission the character holds).
 * Carries no dialogue; conversations about a commission happen in its
 * cutscenes.
 */
export interface Commission {
    /** Stable slug; matches the file name. */
    id: CommissionId;
    title: string;
    /** Who posts the commission. */
    issuer: string;
    /** What the player knows about it, for its card and Journal entry. */
    background: string;
    /**
     * The small ink sketch beside its Journal entry: a file name in
     * `game-content/assets/chronicle/sketches/`. The API serves it as the
     * URL path it is loaded from. An entry without one has no sketch.
     */
    journalSketch?: string;
    /** What the commission asks for. Snapshotted onto each offer. */
    goals: CommissionGoal[];
    /**
     * Cutscene (id; a character loads it from `GET .../characters/:characterId/cutscenes/:cutsceneId`) that plays once while
     * the commission is offered, before the character can accept it: the
     * meeting where the commission is asked of them.
     */
    offerCutscene?: string;
    /**
     * Accepting drops the character straight into the run: no lobby step, no
     * "Enter the Crucible" button — the gathering screen starts the Crucible
     * itself and hands off to gameplay. Only appropriate for a solo
     * (`maxPlayers: 1`) Crucible; with room for a party there is nobody to
     * wait for and the direct entry would lock everyone else out.
     */
    hasDirectEntry: boolean;
}

/**
 * Body of `POST /api/crucibles` that accepts (or returns to) a commission by
 * provisioning its Crucible.
 * Options for the run the commission provisions (such as who comes along on a
 * party commission) belong here as they're added. Unknown fields are
 * rejected, so a client never believes an option took effect when this
 * server doesn't support it.
 */
export interface AcceptCommissionRequest {
    readonly commissionId: CommissionId;
    /** The character accepting it, who joins the run as its creator. */
    readonly characterId: string;
}

/**
 * A commission file as authored: what players see, plus the purpose-built
 * Crucible accepting it provisions. The server keeps `crucible` to itself;
 * clients get a `Commission`.
 */
export interface CommissionDefinition extends Commission {
    crucible: CommissionCrucibleConfig;
    /**
     * What the player is told while working toward this commission before it
     * is offered (see `UpcomingCommission`), such as the clerk's advice to
     * try a few more Crucibles. `{victoriesRemaining}` is replaced with the
     * Crucible victories its rule still needs, so the text follows the rules
     * file. Server-only until it is rendered.
     */
    upcomingNote?: string;
}

/** One requirement a commission rule needs met before its commission is offered. */
export type CommissionRequirement =
    /** The character has completed this commission. */
    | { type: 'commission-completed'; commission: CommissionId }
    /** The character has won at least `count` Crucibles in all (any Crucible, solo or party). */
    | { type: 'crucible-victories'; count: number };

/** What a character needs before one commission is offered to them. */
export interface CommissionRule {
    commission: CommissionId;
    /** All must be met. None means every character qualifies. */
    requires?: CommissionRequirement[];
}

/**
 * Every commission's offer rule, authored in one file:
 * `game-content/data/commissions/commission-rules.json`. A character is
 * offered each commission they qualify for when their commissions are read;
 * a commission with no rule is never offered. Server-only.
 */
export interface CommissionRules {
    commissions: CommissionRule[];
}

/**
 * A requirement an upcoming commission still waits on, with the character's
 * progress toward it.
 */
export type CommissionRequirementProgress =
    /** Crucibles won so far (`current`, capped at `count`) toward the `count` needed. */
    | { type: 'crucible-victories'; count: number; current: number };

/**
 * A commission the character is working toward: every commission its rule
 * waits on is complete, but it isn't offered yet. It names only what is
 * still needed, never the commission, so the story can't be read ahead.
 * Served by `GET .../characters/:characterId/commissions/upcoming`.
 */
export interface UpcomingCommission {
    /** Its unmet requirements, in rule order. */
    requires: CommissionRequirementProgress[];
    /** The commission's `upcomingNote`, its placeholders filled in, when it has one. */
    note?: string;
}

/**
 * Whether a character is still locked out of the Crucibles listing: they hold
 * the intro commission and haven't completed it. The house of exchange only
 * shares where Crucibles surface once it has their name. Characters with no
 * intro commission (created before commissions existed) are never locked.
 */
export function isCrucibleAccessLocked(commissions: readonly CharacterCommission[]): boolean {
    return commissions.some((commission) => commission.commissionId === INTRO_COMMISSION_ID && commission.status !== 'completed');
}

/** The commission whose completion lets a character create Crucibles above `LIMITED_CRUCIBLE_SATURATION`. */
export const SATURATION_LIMIT_COMMISSION_ID: CommissionId = 'mill-road-commission';

/** The only saturation a character still limited by `isCrucibleSaturationLimited` may create a Crucible at. */
export const LIMITED_CRUCIBLE_SATURATION: VeilSaturationId = 'faint';

/**
 * Whether a character may only create Faint Crucibles, never a random roll:
 * they hold the intro commission and haven't completed the Mill Road. The
 * House points the newly touched at a few faint ones first, as its clerk
 * advises. Joining any Crucible stays open. Characters with no intro
 * commission are never limited, as with `isCrucibleAccessLocked`.
 */
export function isCrucibleSaturationLimited(commissions: readonly CharacterCommission[]): boolean {
    return isAwaitingCommission(commissions, SATURATION_LIMIT_COMMISSION_ID);
}

/** The commission whose completion opens the Compendium: the clerk names it in its after-victory cutscene. */
export const COMPENDIUM_UNLOCK_COMMISSION_ID: CommissionId = 'mill-road-commission';

/**
 * Whether the Compendium is still closed to a character: they hold the intro
 * commission and haven't completed the Mill Road. Until the clerk tells them
 * veil-touched keep a record of their visions, they have nowhere to keep one.
 * Visions are still received and stored. Characters with no intro commission
 * are never locked, as with `isCrucibleAccessLocked`.
 */
export function isCompendiumLocked(commissions: readonly CharacterCommission[]): boolean {
    return isAwaitingCommission(commissions, COMPENDIUM_UNLOCK_COMMISSION_ID);
}

/** Whether a character on the intro path (they hold the intro commission) hasn't completed a commission yet. */
function isAwaitingCommission(commissions: readonly CharacterCommission[], commissionId: CommissionId): boolean {
    return commissions.some((commission) => commission.commissionId === INTRO_COMMISSION_ID)
        && !commissions.some((commission) => commission.commissionId === commissionId && commission.status === 'completed');
}

/**
 * Whether a run completes an accepted commission: it is bound to that run and
 * every goal that isn't optional is complete for its holder.
 */
export function isCommissionCompletedByRun(commission: CharacterCommission, crucibleId: string, facts: CommissionGoalFacts): boolean {
    return commission.status === 'accepted'
        && commission.crucibleId === crucibleId
        && isCommissionComplete(commission.goals, resolveCommissionGoals(commission.goals, facts));
}

/**
 * The commission that provisioned a Crucible run, or undefined for an ordinary
 * Crucible. Matches on the bound run regardless of status, since a victory
 * completes the commission before its after-cutscene plays.
 */
export function findCommissionForCrucible(
    commissions: readonly CharacterCommission[],
    crucibleId: string,
): CharacterCommission | undefined {
    return commissions.find((commission) => commission.crucibleId === crucibleId) ?? undefined;
}
